import React, { useMemo } from 'react';
import { formatStatus, formatTimestamp, formatAuditAction } from '../constants/workflowLabels';

const KIND_LABEL = {
  case: 'Case',
  permit: 'Permit',
  violation: 'Violation',
  audit: 'Audit',
};

function shortActor(actor) {
  if (!actor) return null;
  const s = String(actor);
  if (s.length <= 12) return s;
  return `${s.slice(0, 8)}…`;
}

function groupEventsByDay(events) {
  const groups = [];
  let currentDay = null;
  events.forEach((ev) => {
    const day = ev.ts ? new Date(ev.ts).toLocaleDateString() : 'Unknown date';
    if (day !== currentDay) {
      currentDay = day;
      groups.push({ day, events: [] });
    }
    groups[groups.length - 1].events.push(ev);
  });
  return groups;
}

const CaseTimeline = ({ events, caseRow }) => {
  const dayGroups = useMemo(() => groupEventsByDay(events || []), [events]);

  if (!events?.length) {
    return (
      <div className="rhs-empty-state rhs-empty-state--compact">
        <p className="rhs-muted">
          No timeline events yet. Activity appears when permits, violations, or status
          updates are recorded in GovOps.
        </p>
      </div>
    );
  }

  return (
    <div className="rhs-timeline">
      {caseRow && (
        <p className="rhs-case-current">
          Current status:{' '}
          <strong>{formatStatus(caseRow.status)}</strong>
        </p>
      )}
      {dayGroups.map(({ day, events: dayEvents }) => (
        <section key={day} className="rhs-timeline-day-group" aria-label={`Events on ${day}`}>
          <h3 className="rhs-timeline-day-heading">{day}</h3>
          <ol className="rhs-timeline-list rhs-timeline-list--polished">
            {dayEvents.map((ev) => {
              const detailText =
                formatStatus(ev.detail) !== ev.detail
                  ? formatStatus(ev.detail)
                  : ev.detail;
              return (
                <li
                  key={ev.id}
                  className={`rhs-timeline-item rhs-timeline-${ev.kind}`}
                >
                  <div className="rhs-timeline-marker" aria-hidden="true" />
                  <div className="rhs-timeline-content">
                    <div className="rhs-timeline-meta">
                      <time dateTime={ev.ts}>{formatTimestamp(ev.ts)}</time>
                      <span className="rhs-timeline-kind">
                        {KIND_LABEL[ev.kind] || ev.kind}
                      </span>
                    </div>
                    <strong>{ev.label || formatAuditAction(ev.action)}</strong>
                    {detailText && (
                      <span className="rhs-timeline-detail">{detailText}</span>
                    )}
                    {ev.actor && (
                      <span className="rhs-timeline-actor">
                        Actor: {shortActor(ev.actor)}
                      </span>
                    )}
                  </div>
                </li>
              );
            })}
          </ol>
        </section>
      ))}
    </div>
  );
};

export default CaseTimeline;
